import { type StudyPlan, type StudyPlanTask } from "./study-plan";
import {
  attendanceKeyOf,
  studyTaskCodeLang,
  attendanceSlugOf,
  buildScheduledTaskByKey,
  buildScheduledTasks,
  taskKeyOf,
  tasksOnDate,
  taskSortValue,
  type BasePlacement,
  type PlanDynamicState,
  type StudyScheduleSnapshot,
} from "./study-plan-state";
import { shouldShowAttendanceTool } from "./route-layout";

/**
 * 최초 구독 또는 사용자가 명시적으로 배치를 초기화할 때의 기본 배치.
 * 기본(`page`)은 계획 페이지의 Day 날짜 그대로다(JCG-3652) — 그래서 "원래 감자 일정으로 복귀"가
 * 정확히 계획 페이지 모양이 된다. buildScheduledTasks 의 사본 없는 경로와 같은 규칙이므로
 * 미리보기(StartPlanCTA)와 실제 구독 결과가 일치한다. `legacy-even` 은 사본 없는 옛 구독을
 * 굳힐 때만 쓴다(BasePlacement 참고).
 */
export function createScheduleSnapshot(
  plan: StudyPlan,
  startDateISO: string,
  basePlacement: BasePlacement = "page",
): StudyScheduleSnapshot {
  const positions: StudyScheduleSnapshot["positions"] = {};
  for (const task of buildScheduledTasks(
    plan,
    {
      subscribed: true,
      startDateISO,
      overrides: {},
      attendedAt: {},
    },
    basePlacement,
  )) {
    positions[task.taskKey] = {
      dateISO: task.effectiveDateISO,
      sortOrder: null,
      attendanceBacked: task.attendanceBacked,
    };
  }
  return { schemaVersion: 1, plan: structuredClone(plan), positions };
}

/** 손상된 사본을 최신 계획으로 대체하면 일정·완료 기록을 재해석하므로 실패시킨다. */
export function readScheduleSnapshot(
  value: unknown,
  planId: string,
  examSessionId: string,
): StudyScheduleSnapshot | null {
  if (value == null) return null;
  const snapshot = value as StudyScheduleSnapshot;
  if (
    snapshot.schemaVersion !== 1 ||
    snapshot.plan?.planId !== planId ||
    snapshot.plan?.examSessionId !== examSessionId ||
    !Array.isArray(snapshot.plan?.days) ||
    !snapshot.positions
  )
    throw new Error("저장된 학습 일정 형식이 올바르지 않습니다.");
  for (const day of snapshot.plan.days) {
    for (const task of day.tasks) {
      const position =
        snapshot.positions[taskKeyOf(day.dayNumber, task.path, task.codeLang)];
      if (
        !position ||
        !/^\d{4}-\d{2}-\d{2}$/.test(position.dateISO) ||
        typeof position.attendanceBacked !== "boolean" ||
        (position.sortOrder !== null && !Number.isFinite(position.sortOrder))
      ) {
        throw new Error("저장된 학습 일정의 배치 정보가 올바르지 않습니다.");
      }
    }
  }
  return snapshot;
}

interface TaskGroup {
  key: string;
  identity: string;
  dayNumber: number;
  tasks: StudyPlanTask[];
}

function groups(plan: StudyPlan): TaskGroup[] {
  const result = new Map<string, TaskGroup>();
  for (const day of plan.days)
    for (const task of day.tasks) {
      const key = taskKeyOf(day.dayNumber, task.path, task.codeLang);
      const group = result.get(key) ?? {
        key,
        dayNumber: day.dayNumber,
        identity: attendanceKeyOf(
          attendanceSlugOf(task.path),
          studyTaskCodeLang(task, plan.examSessionId),
        ),
        tasks: [],
      };
      group.tasks.push(task);
      result.set(key, group);
    }
  return [...result.values()];
}

function titleKeyOf(group: TaskGroup): string {
  return `${group.dayNumber}:${group.tasks[0].title}`;
}

/** 같은 일차·제목이 하나뿐이면 그 그룹, 여럿이면 null(모호하므로 판단 보류). */
function uniqueByTitleKey(list: TaskGroup[]): Map<string, TaskGroup | null> {
  const result = new Map<string, TaskGroup | null>();
  for (const group of list) {
    const key = titleKeyOf(group);
    result.set(key, result.has(key) ? null : group);
  }
  return result;
}

/**
 * 사용자가 직접 남긴 완료·건너뜀 기록. 이 기록이 있으면 항목을 걷어내지 않는다.
 * 완료 판정은 buildScheduledTaskByKey 와 같은 규칙을 쓴다. 과거에 출석 연동을
 * 해제한 첫 일정도 출석 기록이 있으면 진행으로 본다.
 */
function hasProgress(
  group: TaskGroup,
  snapshot: StudyScheduleSnapshot,
  dyn: PlanDynamicState,
): boolean {
  // 출석 없이 명시적으로 완료했던 옛 기록도 사본에서 삭제하지 않는다.
  if (["done", "skipped"].includes(dyn.overrides[group.key]?.status ?? ""))
    return true;
  const task = buildScheduledTaskByKey(snapshot.plan, {
    ...dyn,
    scheduleSnapshot: snapshot,
  }, group.key);
  return Boolean(task?.done || task?.skipped);
}

/**
 * 같은 일차의 같은 제목 항목이 새 경로로 바뀐 경우만 개인 사본의 옛 항목을 대체로 본다.
 * 회차 중 요약 PDF 링크를 지난 회차에서 이번 회차로 바꾸는 교체가 여기에 해당하며,
 * 걷어내지 않으면 같은 제목 두 줄이 남고 한 줄이 지난 회차를 가리킨다. (JCG-3595)
 * 페이지가 현재 계획 어디에도 없고, 같은 일차·제목 후보가 양쪽 모두 하나뿐이며,
 * 완료·건너뜀 기록이 없을 때만 적용한다. 그 밖의 삭제 항목은 종전대로 보존한다.
 * 계획이 그대로인 대다수 조회에서는 후보가 없으므로 집합·Map을 만들지 않고 빠진다.
 */
function supersededGroups(
  previous: TaskGroup[],
  current: TaskGroup[],
  used: Set<string>,
  snapshot: StudyScheduleSnapshot,
  dyn: PlanDynamicState,
): { old: TaskGroup; replacement: TaskGroup }[] {
  let identities: Set<string> | null = null;
  const removed: TaskGroup[] = [];
  for (const group of previous) {
    if (used.has(group.key)) continue;
    identities ??= new Set(current.map((item) => item.identity));
    if (identities.has(group.identity)) continue;
    if (hasProgress(group, snapshot, dyn)) continue;
    removed.push(group);
  }
  if (!removed.length) return [];

  const olds = uniqueByTitleKey(removed);
  const replacements = uniqueByTitleKey(
    current.filter((group) => olds.has(titleKeyOf(group))),
  );
  return [...olds].flatMap(([titleKey, old]) => {
    const replacement = replacements.get(titleKey);
    return old && replacement ? [{ old, replacement }] : [];
  });
}

/**
 * 기존 항목의 날짜·순서·키·출석 연동을 그대로 두고 새 학습 항목만 추가한다.
 * 같은 페이지의 일차 이동은 신규 학습으로 보지 않는다. 회독은 페이지/언어별 발생 횟수로
 * 구분하고 기존 키를 우선 매칭한다. 원본에서 삭제된 항목도 개인 사본에는 보존하되,
 * 같은 일차·제목이 새 경로로 바뀐 항목만 대체로 보고 걷어낸다(supersededGroups).
 * 반환값이 원본과 같으면 DB를 쓰지 않는다. 새 배치는 최초 삽입 시 저장해 이후 조회에 고정된다.
 */
export function addNewScheduleTasks(
  snapshot: StudyScheduleSnapshot,
  currentPlan: StudyPlan,
  dyn: PlanDynamicState,
  todayISO: string,
): StudyScheduleSnapshot {
  if (
    snapshot.plan.planId !== currentPlan.planId ||
    snapshot.plan.examSessionId !== currentPlan.examSessionId
  ) {
    throw new Error("다른 회차의 학습 계획을 합칠 수 없습니다.");
  }
  // 종료 회차는 동결된 공통 계획과 기존 개인 계획을 유지하며 신규 항목을 병합하지 않는다.
  if (currentPlan.examDateISO < todayISO) return snapshot;

  const previous = groups(snapshot.plan);
  const current = groups(currentPlan);
  const used = new Set<string>();
  const mapping = new Map<string, string>();
  const previousByKey = new Map(previous.map((group) => [group.key, group]));
  // 먼저 모든 exact key를 확보해야, 이동된 항목이 후속 회독의 키를 먼저 차지하지 않는다.
  for (const group of current)
    if (previousByKey.has(group.key)) {
      mapping.set(group.key, group.key);
      used.add(group.key);
    }
  for (const group of current)
    if (!mapping.has(group.key)) {
      const match = previous.find(
        (old) => old.identity === group.identity && !used.has(old.key),
      );
      if (match) {
        mapping.set(group.key, match.key);
        used.add(match.key);
      }
    }
  const extraLinks = current.flatMap((group) => {
    const oldKey = mapping.get(group.key);
    const old = oldKey ? previousByKey.get(oldKey) : undefined;
    if (!old) return [];
    const paths = new Set(old.tasks.map((task) => task.path));
    const tasks = group.tasks.filter((task) => {
      if (paths.has(task.path)) return false;
      paths.add(task.path);
      return true;
    });
    return tasks.length ? [{ old, tasks }] : [];
  });
  const superseded = supersededGroups(previous, current, used, snapshot, dyn);
  if (
    current.every((group) => mapping.has(group.key)) &&
    extraLinks.length === 0 &&
    superseded.length === 0
  )
    return snapshot;

  const next = structuredClone(snapshot);
  // 경로만 바뀐 항목은 옛 경로를 걷어내고, 아직 없는 새 경로가 그 자리(날짜·순서)를 이어받는다.
  // 출석 연동 여부는 상속하지 않고 새 경로 기준으로 아래에서 다시 계산한다.
  const inheritedPositions = new Map<
    string,
    { dateISO: string; sortOrder: number | null }
  >();
  for (const { old, replacement } of superseded) {
    // 개인 이동·재정렬은 옛 키의 override 에 있으므로 새 키의 기본 배치로 옮겨 화면을 유지한다.
    const position = next.positions[old.key];
    const override = dyn.overrides[old.key];
    if (!mapping.has(replacement.key))
      inheritedPositions.set(replacement.key, {
        dateISO: override?.movedToDate ?? position.dateISO,
        sortOrder: override?.sortOrder ?? position.sortOrder,
      });
    delete next.positions[old.key];
    const day = next.plan.days.find((d) => d.dayNumber === old.dayNumber)!;
    day.tasks = day.tasks.filter(
      (task) => taskKeyOf(old.dayNumber, task.path, task.codeLang) !== old.key,
    );
  }
  // 같은 페이지의 새 앵커 링크도 보여주되 기존 논리 task의 날짜·완료를 그대로 공유한다.
  for (const { old, tasks } of extraLinks) {
    next.plan.days
      .find((day) => day.dayNumber === old.dayNumber)!
      .tasks.push(...structuredClone(tasks));
  }
  // 관련 일차의 미완료 항목 앞뒤에 새 항목을 끼워 넣을 날짜·정렬값을 고른다.
  const placeNewGroup = (group: TaskGroup, i: number) => {
    const scheduled = buildScheduledTasks(next.plan, {
      ...dyn,
      scheduleSnapshot: next,
    });
    // 같은 논리 페이지의 앵커 묶음을 유지한다. 앞 삽입은 첫 항목, 뒤 삽입은 마지막 항목 기준.
    const scheduledByKey = new Map<string, typeof scheduled>();
    for (const task of [...scheduled].sort(
      (a, b) => taskSortValue(a) - taskSortValue(b),
    )) {
      const tasks = scheduledByKey.get(task.taskKey) ?? [];
      tasks.push(task);
      scheduledByKey.set(task.taskKey, tasks);
    }
    const neighbors = current
      .map((candidate, index) => ({ candidate, index }))
      .filter(({ candidate }) => mapping.has(candidate.key))
      .sort(
        (a, b) =>
          Number(b.candidate.dayNumber === group.dayNumber) -
            Number(a.candidate.dayNumber === group.dayNumber) ||
          Math.abs(a.index - i) - Math.abs(b.index - i) ||
          a.index - b.index,
      );
    const candidates = neighbors.map(({ candidate, index }) => {
      const tasks = scheduledByKey.get(mapping.get(candidate.key)!)!;
      return { task: index > i ? tasks[0] : tasks.at(-1)!, index };
    });
    const anchor =
      candidates.find(({ task }) => !task.done && !task.skipped) ??
      candidates[0];
    // 진행 중인 회차의 신규 항목은 최초 추가일/개인 시작일 이전에 배치하지 않는다.
    const earliest = todayISO > dyn.startDateISO ? todayISO : dyn.startDateISO;
    const target = anchor?.task.effectiveDateISO ?? earliest;
    const dateISO = target > earliest ? target : earliest;
    const sameDate = tasksOnDate(scheduled, dateISO);
    let sortOrder = sameDate.length ? taskSortValue(sameDate.at(-1)!) + 1 : 0;
    if (anchor?.task.effectiveDateISO === dateISO) {
      const anchorOrder = taskSortValue(anchor.task);
      const before = anchor.index > i;
      const adjacent = before
        ? sameDate.filter((task) => taskSortValue(task) < anchorOrder).at(-1)
        : sameDate.find((task) => taskSortValue(task) > anchorOrder);
      sortOrder = adjacent
        ? (anchorOrder + taskSortValue(adjacent)) / 2
        : anchorOrder + (before ? -1 : 1);
    }
    return { dateISO, sortOrder };
  };
  for (let i = 0; i < current.length; i++) {
    const group = current[i];
    if (mapping.has(group.key)) continue;
    // 경로만 바뀐 항목은 옛 항목의 날짜·순서를 그대로 쓴다. 새 이웃을 다시 찾지 않는다.
    const { dateISO, sortOrder } =
      inheritedPositions.get(group.key) ?? placeNewGroup(group, i);
    let day = next.plan.days.find((d) => d.dayNumber === group.dayNumber);
    if (!day) {
      // 새 원본의 일차 수까지 늘어난 경우도 이전 일차 metadata는 덮지 않는다.
      day = {
        ...structuredClone(
          currentPlan.days.find((d) => d.dayNumber === group.dayNumber)!,
        ),
        tasks: [],
      };
      next.plan.days.push(day);
      next.plan.totalDays = Math.max(next.plan.totalDays, group.dayNumber);
    }
    day.tasks.push(...structuredClone(group.tasks));
    const alreadyAttendanceBacked =
      previous.some(
        (old) =>
          old.identity === group.identity &&
          next.positions[old.key]?.attendanceBacked,
      ) ||
      current.some(
        (added) =>
          added.identity === group.identity &&
          next.positions[added.key]?.attendanceBacked,
      );
    next.positions[group.key] = {
      dateISO,
      sortOrder,
      attendanceBacked:
        shouldShowAttendanceTool(group.tasks[0].path) &&
        !alreadyAttendanceBacked,
    };
    mapping.set(group.key, group.key);
    // planPageOrder 등은 plan 객체별로 캐시한다. 수정 뒤에는 새 identity를 사용한다.
    next.plan = { ...next.plan };
  }
  return next;
}

/** 명시적인 일정 초기화도 기존 출석 연동 키를 바꾸지 않는다. */
export function resetSnapshotDates(
  snapshot: StudyScheduleSnapshot,
  startDateISO: string,
): StudyScheduleSnapshot {
  const next = createScheduleSnapshot(snapshot.plan, startDateISO);
  for (const key of Object.keys(next.positions)) {
    next.positions[key].attendanceBacked =
      snapshot.positions[key].attendanceBacked;
  }
  return next;
}

/** 서버의 명시적 초기화가 성공한 뒤 화면에도 동일한 날짜/상태를 반영한다. */
export function resetPlanDynamicSchedule(
  plan: StudyPlan,
  dyn: PlanDynamicState,
): PlanDynamicState {
  const overrides: PlanDynamicState["overrides"] = {};
  for (const [key, override] of Object.entries(dyn.overrides)) {
    // 완료 기록(completedAt·completionDate)은 일정 조정이 아니므로 남긴다(JCG-3547, JCG-3653).
    if (
      override.status !== "none" ||
      override.completedAt ||
      override.completionDate
    ) {
      overrides[key] = {
        ...override,
        movedToDate: null,
        sortOrder: null,
      };
    }
  }
  return {
    ...dyn,
    startDateISO: plan.startDateISO,
    overrides,
    ...(dyn.scheduleSnapshot
      ? {
          scheduleSnapshot: resetSnapshotDates(
            dyn.scheduleSnapshot,
            plan.startDateISO,
          ),
        }
      : {}),
  };
}
